import {BaseObject} from "@/common/BaseObject";
import {alias, createModelSchema, custom, deserialize, SKIP} from "serializr";
import {Schema} from "@/common/Schema";
import {CurrencyGroupModel} from "@/modules/redactor/models/currency/CurrencyGroupModel";
import {ItemKindModel} from "@/modules/redactor/models/item/core/ItemKindModel";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";

export class CurrencyKindModel extends BaseObject {
    constructor(
        key = null,
        code = null,
        item = null,
        modifier = null,
        group = null,
        weight = null,
        shared = false,
        collectEffect = false,
        owners = [],
        ownersExclude = false,
        maps = []
    ) {
        super();

        this._key = key;
        this._code = code;
        this._item = item;
        this._modifier = modifier;
        this._group = group;
        this._weight = weight;
        this._shared = shared;

        this._amount = 0;
        this._capacity = 0;
        this._recoveryCapacity = 0;
        this._recoveryDuration = 0;

        this._collectEffect = collectEffect;
        this._owners = owners;
        this._ownersExclude = ownersExclude;
        this._maps = maps;
    }

    static schema() {
        return custom(
            (v) => {
                return v && v.key ? v.key : SKIP;
            },

            v => {
                if (typeof v === 'string') {
                    return RedactorUtils.getCurrencyKindByKey(v);
                } else if (typeof v === 'object' && v !== null) {
                    return deserialize(CurrencyKindModel, v);
                } else {
                    return null;
                }
            }
        );
    }

    static is(value) {
        return value instanceof CurrencyKindModel &&
            RedactorUtils.getCurrencyKinds().some(v => v.key === value.key);
    }

    static isNone(value) {
        return value instanceof CurrencyKindModel && value.key === "none";
    }

    static getNone(items) {
        return items.find(item => item?.key === 'none')?.key || "none";
    }

    get key() {
        return this._key;
    }

    set key(value) {
        this._key = value;
    }

    get code() {
        return this._code;
    }

    set code(value) {
        this._code = value;
    }

    get item() {
        return this._item;
    }

    set item(value) {
        this._item = value;
    }

    get modifier() {
        return this._modifier;
    }

    set modifier(value) {
        this._modifier = value;
    }

    get group() {
        return this._group;
    }

    set group(value) {
        this._group = value;
    }

    get weight() {
        return this._weight;
    }

    set weight(value) {
        this._weight = value;
    }

    get shared() {
        return this._shared;
    }

    set shared(value) {
        this._shared = value;
    }

    get amount() {
        return this._amount;
    }

    set amount(value) {
        this._amount = value;
    }

    get capacity() {
        return this._capacity;
    }

    set capacity(value) {
        this._capacity = value;
    }

    get recoveryCapacity() {
        return this._recoveryCapacity;
    }

    set recoveryCapacity(value) {
        this._recoveryCapacity = value;
    }

    get recoveryDuration() {
        return this._recoveryDuration;
    }

    set recoveryDuration(value) {
        this._recoveryDuration = value;
    }

    get collectEffect() {
        return this._collectEffect;
    }

    set collectEffect(value) {
        this._collectEffect = value;
    }

    get owners() {
        return this._owners;
    }

    set owners(value) {
        this._owners = value;
    }

    get ownersExclude() {
        return this._ownersExclude;
    }

    set ownersExclude(value) {
        this._ownersExclude = value;
    }

    get maps() {
        return this._maps;
    }

    set maps(value) {
        this._maps = value
          .filter(m => RedactorUtils.getMapKindByCode(m))
          .filter(m => m !== null);
    }

    resetToValidAutomatic() {
        this._maps = this._maps
          .filter(m => RedactorUtils.getMapKindByCode(m))
          .filter(m => m !== null); // Remove invalid map codes
    }


    valid() {
        if (typeof this.key !== "string" || !this.key.trim()) {
            return false;
        }

        if (!Number.isInteger(this.code) || this.code < 0) {
            return false;
        }

        if (!this.item.valid()) {
            return false;
        }

        if (!Number.isInteger(this.weight) || this.weight < 0) {
            return false;
        }

        if (!Number.isInteger(this.amount) || this.amount < 0) {
            return false;
        }

        if (!Number.isInteger(this.capacity) || this.capacity < 0) {
            return false;
        }

        if (!Number.isInteger(this.recoveryCapacity) || this.recoveryCapacity < 0) {
            return false;
        }

        if (!Number.isInteger(this.recoveryDuration) || this.recoveryDuration < 0) {
            return false;
        }

        if (!Array.isArray(this.owners)) {
            return false;
        }

        if (!Array.isArray(this.maps)) {
            return false;
        }

        return true;
    }

    equal(value) {
        if (value instanceof CurrencyKindModel) {
            return this.key === value.key;
        }
        return false;
    }

    copy() {
        let value = super.copy();
        value._enabled = false;
        return value;
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new CurrencyKindModel());
        } else if (target instanceof CurrencyKindModel) {
            return super.clone(target);
        } else {
            console.log(false);
            return undefined;
        }
    }
}

createModelSchema(CurrencyKindModel, {
    _key: alias("key", Schema.string()),
    _code: alias("code", Schema.number()),
    _item: alias("item", ItemKindModel.schema()), //ItemKind
    _modifier: alias("modifier", Schema.string()),
    _group: alias("group", CurrencyGroupModel.schema()),
    _weight: alias("weight", Schema.number()),
    _shared: alias("shared", Schema.primitive()),
    _amount: alias("amount", Schema.primitive()),
    _capacity: alias("capacity", Schema.primitive()),
    _recoveryCapacity: alias("recovery_capacity", Schema.primitive()),
    _recoveryDuration: alias("recovery_duration", Schema.primitive()),
    _collectEffect: alias("collect_effect", Schema.primitive()),
    _owners: alias("owners", Schema.primitive()),
    _ownersExclude: alias("owners_exclude", Schema.primitive()),
    _maps: alias("maps", Schema.primitive()),
});